import { GetRoute, RouteVariables } from 'types/routes'
import { Language } from 'types/language'
import { ROUTES } from 'constants/routes'
import { stringify } from 'qs'
import { NextRouter } from 'next/router'

/**
 * Gets route based upon current path and language
 *
 * @param {string} path
 * @param {Language} language
 * @returns {string|'home'}
 */
const getRouteKey = (path: string, language: Language): string => {
  // Check if path is a subroute of the producers path
  const regex = new RegExp(ROUTES.PRODUCERS[language] + '/.+$')

  if (regex.exec(path)) {
    return path.includes(ROUTES.REGISTRATION[language]) ? 'registration' : 'producerInfo'
  }

  // Return the route key or return home if it couldn't be found
  return (
    Object.keys(routes(language)).find((key: string) => {
      const pathHasNoStartingSlash = !routes(language)[key].startsWith('/')
      const pathToMatch = pathHasNoStartingSlash ? `/${routes(language)[key]}` : routes(language)[key]
      return path === pathToMatch
    }) ?? 'home'
  )
}

/**
 * Translates path to chosen language
 *
 * @param {NextRouter} router
 * @param {Language} newLanguage
 * @returns {Pick<NextRouter, 'asPath'>}
 */
export const translateRoute = (router: NextRouter, newLanguage: Language): Pick<NextRouter, 'asPath'> => {
  const pathInOldLanguage = router.asPath.split('?')[0]

  // Add old language to path
  const pathWithLanguage = `/${router.locale}${pathInOldLanguage}`

  // Gets route key
  const routeKey = getRouteKey(pathWithLanguage.replace(/\/$/, ''), router.locale as Language)

  // Get slug and rest of query
  const { slug, ...query } = router.query

  return {
    asPath: `${replaceRouteVariable(routes(newLanguage)[routeKey], RouteVariables.SLUG, slug as string)}${stringify(query, { addQueryPrefix: true })}`
  }
}

/**
 * Replaces the slug in the route if it's not undefined
 *
 * @param route
 * @param variable
 * @param slug
 */
const replaceRouteVariable = (route: string, variable: RouteVariables, slug: string | undefined): string => {
  return route.replace(variable, slug !== undefined ? slug : variable)
}

/**
 * Replaces multiple slugs in the route if it's not undefined
 *
 * @param route
 * @param variables
 * @param slugs
 */
const replaceNestedRouteVariable = (route: string, variables: RouteVariables[], slugs: string[] | undefined): string => {
  return variables.reduce((prev: string, curr, idx) => prev.replace(curr, slugs?.length ? slugs[idx] : curr), route)
}

/**
 * Returns all routes
 * @param {string} lang
 * @param {string} slug
 * @param {string[]} slugs
 * @returns {GetRoute}
 */
export const routes = (lang: string, slug?: string, slugs?: string[]): GetRoute =>
  ({
    about: `/${lang}/${ROUTES.ABOUT[lang]}`,
    ancienneBelgique: `/${lang}/${ROUTES.ANCIENNE_BELGIQUE[lang]}`,
    becomeProducer: `/${lang}/${ROUTES.BECOME_PRODUCER[lang]}`,
    becomeProducerForm: `/${lang}/${ROUTES.BECOME_PRODUCER_REQUEST[lang]}`,
    benefitsAtWork: `/${lang}/benefits-at-work`,
    blog: `/${lang}/${ROUTES.BLOG[lang]}`,
    blogArticle: replaceRouteVariable(`/${lang}/${ROUTES.BLOG_ARTICLE[lang]}`, RouteVariables.ARTICLE, slug),
    boltDriveFlex: `/${lang}/bolt-drive-flex`,
    boltFixOptIn: `/${lang}/bolt-fix-opt-in`,
    boltXPlenty: `/${lang}/bolt-x-plenty`,
    brusselsAirport: `/${lang}/${ROUTES.BRUSSELS_AIRPORT[lang]}`,
    careerDetail: replaceRouteVariable(`/${lang}/${ROUTES.CAREER_DETAIL[lang]}`, RouteVariables.SLUG, slug),
    careers: `/${lang}/${ROUTES.CAREERS[lang]}`,
    complaint: `/${lang}/${ROUTES.COMPLAINT[lang]}`,
    dynamicPricing: `/${lang}/${ROUTES.DYNAMIC_PRICES[lang]}`,
    encyclopedia: `/${lang}/${ROUTES.ENCYCLOPEDIA[lang]}`,
    encyclopediaArticle: replaceNestedRouteVariable(
      `/nl/${ROUTES.ENCYCLOPEDIA_ARTICLE['nl']}`,
      [RouteVariables.CATEGORY, RouteVariables.ARTICLE],
      slugs
    ),
    encyclopediaCategory: replaceRouteVariable(`/nl/${ROUTES.ENCYCLOPEDIA_CATEGORY['nl']}`, RouteVariables.CATEGORY, slug),
    energyCrisisFaq: `/${lang}/${ROUTES.ENERGY_CRISIS_FAQ[lang]}`,
    faq: `/${lang}/${ROUTES.FAQ[lang]}`,
    festivals: `/${lang}/${ROUTES.FESTIVALS[lang]}`,
    home: `/${lang}`,
    injectionTariff: `/${lang}/${ROUTES.INJECTION_TARIFF[lang]}`,
    instalmentConfirmation: `/${lang}/instalment-confirmation`,
    instalmentOptOut: `/${lang}/instalment-opt-out`,
    move: `/${lang}/${ROUTES.MOVE[lang]}`,
    moveLP: `/${lang}/${ROUTES.MOVE_LP[lang]}`,
    noEan: `/${lang}/no-ean`,
    oldTerms: replaceRouteVariable(`/${lang}/${ROUTES.OLD_TERMS_AND_CONDITIONS[lang]}`, RouteVariables.VERSION, slug),
    pluspas: `/${lang}/pluspas`,
    priceList: `/${lang}/${ROUTES.PRICELISTS[lang]}`,
    pricing: `/${lang}/${ROUTES.PRICING[lang]}`,
    privacy: `/${lang}/${ROUTES.PRIVACY[lang]}`,
    producerInfo: replaceRouteVariable(`/${lang}/${ROUTES.PRODUCER_INFO[lang]}`, RouteVariables.SLUG, slug),
    producers: `/${lang}/${ROUTES.PRODUCERS[lang]}`,
    productDetail: replaceRouteVariable(`/${lang}/${ROUTES.PRODUCT_DETAIL[lang]}`, RouteVariables.SLUG, slug),
    referral: `/${lang}/${ROUTES.REFERRAL[lang]}`,
    registration: replaceRouteVariable(`/${lang}/${ROUTES.REGISTRATION[lang]}`, RouteVariables.SLUG, slug),
    registrationLoading: `/${lang}/${ROUTES.REGISTRATION_LOADING[lang]}`,
    settlement: `/${lang}/${ROUTES.SETTLEMENT[lang]}`,
    simulation: `/${lang}/${ROUTES.SIMULATION[lang]}`,
    springCampaign: `/nl/${ROUTES.SPRING_CAMPAIGN['nl']}`,
    terms: `/${lang}/${ROUTES.TERMS_AND_CONDITIONS[lang]}`,
    uspsCustomerCare: `/${lang}/usps/customer-care`,
    uspsFairPrice: `/${lang}/usps/fair-price`,
    uspsFun: `/${lang}/usps/fun`,
    uspsLocal: `/${lang}/usps/local`,
    uspsOverview: `/${lang}/usps`
  }) satisfies GetRoute
